import React, { useState, useEffect } from 'react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import { GlassBadge } from '../ui/GlassBadge';
import { apiClient } from '../../services/api';
import { Save, RefreshCw, Eye, EyeOff } from 'lucide-react';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface StripeConfigResponse {
    publishable_key: string;
    growth_price_id: string;
    pro_price_id: string;
    enterprise_price_id: string;
    secret_key_hint: string;
    webhook_secret_hint: string;
    has_secret_key: boolean;
    has_webhook_secret: boolean;
    mode: 'live' | 'test' | 'not_configured';
}

const modeVariant = (mode: string) => {
    if (mode === 'live') return 'success' as const;
    if (mode === 'test') return 'warning' as const;
    return 'default' as const;
};

const modeLabel = (mode: string) => {
    if (mode === 'live') return 'Live Mode';
    if (mode === 'test') return 'Test Mode';
    return 'Not Configured';
};

const StripeConfigPanel: React.FC = () => {
    const [remote, setRemote] = useState<StripeConfigResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Editable fields
    const [secretKey, setSecretKey] = useState('');
    const [publishableKey, setPublishableKey] = useState('');
    const [webhookSecret, setWebhookSecret] = useState('');
    const [growthPriceId, setGrowthPriceId] = useState('');
    const [proPriceId, setProPriceId] = useState('');
    const [enterprisePriceId, setEnterprisePriceId] = useState('');

    const [showSecret, setShowSecret] = useState(false);
    const [showWebhook, setShowWebhook] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const res = await apiClient.get<StripeConfigResponse>('/settings/platform/stripe-config');
            setRemote(res.data);
            setPublishableKey(res.data.publishable_key);
            setGrowthPriceId(res.data.growth_price_id);
            setProPriceId(res.data.pro_price_id);
            setEnterprisePriceId(res.data.enterprise_price_id);
            // Leave secret fields blank — user must re-enter to change
            setSecretKey('');
            setWebhookSecret('');
        } catch (err: any) {
            setMessage({ type: 'error', text: getApiError(err, 'Failed to load Stripe config') });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleSave = async () => {
        try {
            setSaving(true);
            setMessage(null);
            await apiClient.put('/settings/platform/stripe-config', {
                secret_key: secretKey,
                publishable_key: publishableKey,
                webhook_secret: webhookSecret,
                growth_price_id: growthPriceId,
                pro_price_id: proPriceId,
                enterprise_price_id: enterprisePriceId,
            });
            setMessage({ type: 'success', text: 'Stripe configuration saved.' });
            await load();
        } catch (err: any) {
            setMessage({ type: 'error', text: getApiError(err, 'Failed to save') });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <GlassCard padding="lg"><p style={{ color: 'var(--text-secondary)' }}>Loading Stripe configuration...</p></GlassCard>;
    }

    const secretPlaceholder = remote?.has_secret_key
        ? `Current: ${remote.secret_key_hint} — leave blank to keep`
        : 'sk_live_... or sk_test_...';

    const webhookPlaceholder = remote?.has_webhook_secret
        ? `Current: ${remote.webhook_secret_hint} — leave blank to keep`
        : 'whsec_...';

    return (
        <div style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Status banner */}
            <GlassCard padding="md">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Stripe mode:</span>
                    <GlassBadge variant={modeVariant(remote?.mode || 'not_configured')}>
                        {modeLabel(remote?.mode || 'not_configured')}
                    </GlassBadge>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginLeft: 'auto' }}>
                        Keys are stored encrypted in the platform database.
                    </span>
                </div>
            </GlassCard>

            {/* API Keys */}
            <GlassCard padding="lg">
                <h3 style={{ margin: '0 0 0.25rem', color: 'var(--text)', fontSize: '1rem', fontWeight: 600 }}>API Keys</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: '0 0 1.25rem' }}>
                    Find these in your <strong>Stripe Dashboard → Developers → API keys</strong>.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {/* Secret key */}
                    <div style={{ position: 'relative' }}>
                        <GlassInput
                            label="Secret Key"
                            type={showSecret ? 'text' : 'password'}
                            value={secretKey}
                            onChange={setSecretKey}
                            placeholder={secretPlaceholder}
                        />
                        <button
                            type="button"
                            onClick={() => setShowSecret(v => !v)}
                            style={{
                                position: 'absolute', right: 10, top: 34,
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: 'var(--text-secondary)', padding: 4,
                            }}
                        >
                            {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    </div>

                    <GlassInput
                        label="Publishable Key"
                        value={publishableKey}
                        onChange={setPublishableKey}
                        placeholder="pk_live_... or pk_test_..."
                    />

                    {/* Webhook secret */}
                    <div style={{ position: 'relative' }}>
                        <GlassInput
                            label="Webhook Signing Secret"
                            type={showWebhook ? 'text' : 'password'}
                            value={webhookSecret}
                            onChange={setWebhookSecret}
                            placeholder={webhookPlaceholder}
                        />
                        <button
                            type="button"
                            onClick={() => setShowWebhook(v => !v)}
                            style={{
                                position: 'absolute', right: 10, top: 34,
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: 'var(--text-secondary)', padding: 4,
                            }}
                        >
                            {showWebhook ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    </div>
                </div>

                <p style={{ color: 'var(--text-secondary)', fontSize: '0.78rem', marginTop: '0.75rem' }}>
                    Webhook endpoint: <code style={{ background: 'var(--panel-strong)', padding: '1px 6px', borderRadius: 4 }}>
                        POST /api/billing/webhook
                    </code> — register this URL in Stripe Dashboard → Webhooks.
                </p>
            </GlassCard>

            {/* Price IDs */}
            <GlassCard padding="lg">
                <h3 style={{ margin: '0 0 0.25rem', color: 'var(--text)', fontSize: '1rem', fontWeight: 600 }}>Subscription Price IDs</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: '0 0 1.25rem' }}>
                    Find these in <strong>Stripe Dashboard → Products</strong>. Each plan needs a recurring monthly Price ID.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <GlassInput
                        label="Growth Plan Price ID"
                        value={growthPriceId}
                        onChange={setGrowthPriceId}
                        placeholder="price_..."
                    />
                    <GlassInput
                        label="Pro Plan Price ID"
                        value={proPriceId}
                        onChange={setProPriceId}
                        placeholder="price_..."
                    />
                    <GlassInput
                        label="Enterprise Plan Price ID"
                        value={enterprisePriceId}
                        onChange={setEnterprisePriceId}
                        placeholder="price_..."
                    />
                </div>
            </GlassCard>

            {message && (
                <div style={{
                    padding: '0.75rem 1rem',
                    borderRadius: 'var(--radius-md)',
                    background: message.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                    color: message.type === 'success' ? 'var(--success, #10b981)' : 'var(--danger, #ef4444)',
                    fontSize: '0.875rem',
                }}>
                    {message.text}
                </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
                <GlassButton
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving}
                    icon={<Save size={16} />}
                >
                    {saving ? 'Saving...' : 'Save Configuration'}
                </GlassButton>
                <GlassButton
                    variant="secondary"
                    onClick={load}
                    icon={<RefreshCw size={16} />}
                >
                    Reset
                </GlassButton>
            </div>
        </div>
    );
};

export default StripeConfigPanel;
